import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import type { CanvasPoint, Idea, Placement } from '../api'
import { navigate } from '../app/routing'
import { FitIcon, MinusIcon, PlayIcon, PlusIcon } from '../components/icons'
import { IdeaMeta, VideoRings, VideoThumb } from '../components/IdeaBits'
import { PipelineHead } from '../components/PipelineHead'
import { useSeriesView } from '../components/SeriesFilter'
import {
  absolute,
  CARD_WIDTH,
  clampToFrame,
  extent,
  FRAMES,
  freeSpot,
  GAP,
  insertionIndex,
  isFree,
  layOut,
  PAD,
  pinFrame,
  stageAt,
  TAB_HEIGHT,
  type Layout,
} from '../domain/canvas'
import { byStage, STAGE_LABEL, STAGES, type Stage } from '../domain/stages'
import { arrangeIdeas, ideasStore, isPending, loadIdeas, useIdeas } from '../state/ideas'
import { moveToStage } from '../state/moves'
import { panelStore } from '../state/panel'
import { setQueue, useQueue } from '../state/queue'
import { toast } from '../state/toast'
import { openQuickAdd } from './QuickAdd'
import './canvas.css'

/**
 * The pipeline on a laptop: a canvas with a frame per stage, left to right in
 * the order the work runs.
 *
 * A card is dragged to move it: into another frame to move the idea to that
 * stage, anywhere inside Ideas or Scripted to put it there, up or down in
 * Filming to change the reading order. Whatever happens, the card slides from
 * where it was let go to where it lands, so nothing jumps; a drop the
 * pipeline would refuse slides back. A click opens the idea in the panel
 * beside the canvas. The background pans with a drag or a scroll and zooms
 * with a pinch, and the view is kept between visits.
 */
interface View {
  x: number
  y: number
  zoom: number
}

const VIEW_KEY = 'content:canvas'
const START: View = { x: 40, y: 132, zoom: 0.8 }
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'
/** The header floats over the top of the canvas; nothing is revealed under it. */
const HEAD_ROOM = 96
const FOOT_ROOM = 76

function savedView(): View {
  try {
    const view = JSON.parse(localStorage.getItem(VIEW_KEY) ?? 'null') as View | null
    if (view && [view.x, view.y, view.zoom].every(Number.isFinite)) return view
  } catch {
    // A view that cannot be read is the first visit's view.
  }
  return START
}

/*
 * Card heights. A card is as tall as its title needs, one line or two, and
 * the layout has to know that before anything is drawn, so titles are
 * measured in the card's font. Padding 11 + 11, border 2, a 20px line per
 * title line, and the meta line (3 + 17) under it: see canvas.css.
 */
const LINE = 20
const CHROME = 44
const POST_HEIGHT = 104
const TEXT_WIDTH = { free: CARD_WIDTH - 28, filming: FRAMES.filming.width - 2 * PAD - 28 - 34 }

let context: CanvasRenderingContext2D | null = null
const measured = new Map<string, number>()

function titleLines(title: string, width: number): number {
  const key = `${width}:${title}`
  const known = measured.get(key)
  if (known) return known
  context ??= document.createElement('canvas').getContext('2d')
  if (!context) return title.length > 26 ? 2 : 1
  context.font = `500 14.5px ${getComputedStyle(document.documentElement).getPropertyValue('--font-sans')}`
  let lines = 1
  let line = ''
  for (const word of title.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word
    if (!line || context.measureText(next).width <= width) line = next
    else {
      lines += 1
      line = word
    }
  }
  const result = Math.min(lines, 2)
  measured.set(key, result)
  return result
}

function heightOf(idea: Idea, stage: Stage): number {
  if (stage === 'post') return POST_HEIGHT
  return CHROME + LINE * titleLines(idea.title, stage === 'filming' ? TEXT_WIDTH.filming : TEXT_WIDTH.free)
}

/** Titles are measured in the real font, so the layout is redone once it has loaded. */
function useFontsLoaded(): boolean {
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    let current = true
    void document.fonts.ready.then(() => {
      measured.clear()
      if (current) setLoaded(true)
    })
    return () => {
      current = false
    }
  }, [])
  return loaded
}

type Gesture =
  | { kind: 'pan'; x: number; y: number; view: View; moved: boolean }
  | { kind: 'card'; id: number; stage: Stage; x: number; y: number; grab: CanvasPoint; moved: boolean }

interface Drag {
  id: number
  from: Stage
  ghost: CanvasPoint
  target: Stage | null
  refused: boolean
  index?: number
}

export function Canvas({ selected }: { selected: number | null }) {
  const { ideas, phase, error } = useIdeas()
  const { ids, batch } = useQueue()
  const series = useSeriesView()
  const fonts = useFontsLoaded()
  const stages = useMemo(() => byStage(ideas, ids), [ideas, ids])
  // `fonts` is not read inside: it is there to measure again once the font has loaded.
  const layout = useMemo(() => layOut(stages, heightOf), [stages, fonts])
  const inPipeline = useMemo(() => STAGES.flatMap((stage) => stages[stage]), [stages])

  const viewport = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View>(savedView)
  const current = useRef(view)
  current.current = view
  const [gliding, setGliding] = useState(false)
  const gesture = useRef<Gesture | null>(null)
  const [drag, setDragState] = useState<Drag | null>(null)
  // Pointer up reads the drag from here: a fast flick can end before React has
  // rendered the last move.
  const dragRef = useRef<Drag | null>(null)
  const setDrag = (next: Drag | null) => {
    dragRef.current = next
    setDragState(next)
  }
  const [panning, setPanning] = useState(false)

  // Where every card was drawn last time, and where a dropped one was let go.
  const drawn = useRef(new Map<number, CanvasPoint>())
  const letGo = useRef(new Map<number, CanvasPoint>())

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(VIEW_KEY, JSON.stringify(view))
      } catch {
        // Not kept, then. The next visit starts from the default view.
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [view])

  // A card that moved slides from where it was; a new one fades in.
  useLayoutEffect(() => {
    const next = new Map<number, CanvasPoint>()
    for (const [id, card] of layout.cards) next.set(id, absolute(card))
    const firstPaint = drawn.current.size === 0
    for (const node of viewport.current?.querySelectorAll<HTMLElement>('[data-card]') ?? []) {
      const id = Number(node.dataset.card)
      const now = next.get(id)
      if (!now) continue
      const before = letGo.current.get(id) ?? drawn.current.get(id)
      if (!before) {
        if (!firstPaint) {
          node.animate([{ opacity: 0, transform: 'scale(0.97)' }, { opacity: 1, transform: 'none' }], { duration: 240, easing: EASE })
        }
        continue
      }
      const dx = before.x - now.x
      const dy = before.y - now.y
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue
      node.style.zIndex = '5'
      node.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 420, easing: EASE }).onfinish =
        () => {
          node.style.zIndex = ''
        }
    }
    letGo.current.clear()
    drawn.current = next
  })

  const glide = () => {
    setGliding(true)
    setTimeout(() => setGliding(false), 480)
  }

  const toWorld = (clientX: number, clientY: number): CanvasPoint => {
    const box = viewport.current?.getBoundingClientRect()
    const { x, y, zoom } = current.current
    return { x: (clientX - (box?.left ?? 0) - x) / zoom, y: (clientY - (box?.top ?? 0) - y) / zoom }
  }

  const zoomAt = (clientX: number, clientY: number, factor: number) => {
    const box = viewport.current?.getBoundingClientRect()
    if (!box) return
    setView(({ x, y, zoom }) => {
      const next = Math.min(1.6, Math.max(0.3, zoom * factor))
      const px = clientX - box.left
      const py = clientY - box.top
      return { zoom: next, x: px - (px - x) * (next / zoom), y: py - (py - y) * (next / zoom) }
    })
  }

  const fit = () => {
    const box = viewport.current?.getBoundingClientRect()
    if (!box) return
    const { width, height } = extent(layout.heights)
    const zoom = Math.min(1, Math.max(0.3, Math.min((box.width - 80) / width, (box.height - HEAD_ROOM - 60) / height)))
    glide()
    setView({ zoom, x: Math.round((box.width - width * zoom) / 2), y: Math.round(HEAD_ROOM + 10 + TAB_HEIGHT * zoom) })
  }

  /** Pan just far enough that a card is in view, clear of the header and the zoom controls. */
  const reveal = (id: number) => {
    const card = latestLayout.current.cards.get(id)
    const box = viewport.current?.getBoundingClientRect()
    if (!card || !box) return
    const at = absolute(card)
    const { x, y, zoom } = current.current
    const left = x + at.x * zoom
    const top = y + at.y * zoom
    const inView =
      left >= 24 &&
      left + card.width * zoom <= box.width - 24 &&
      top >= HEAD_ROOM &&
      top + card.height * zoom <= box.height - FOOT_ROOM
    if (inView) return
    glide()
    setView({
      zoom,
      x: Math.round(box.width / 2 - (at.x + card.width / 2) * zoom),
      y: Math.round((HEAD_ROOM + box.height - FOOT_ROOM) / 2 - (at.y + card.height / 2) * zoom),
    })
  }

  // Keep the open idea in view: when it opens (after the panel has slid in and
  // narrowed the canvas) and whenever it moves to another frame. The timer is
  // not cleared by the next layout, which the panel's own fetch brings within
  // those 300ms; only a newer reveal replaces it.
  const revealed = useRef('')
  const revealTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const latestLayout = useRef(layout)
  latestLayout.current = layout
  useEffect(() => {
    if (selected === null) {
      revealed.current = ''
      return
    }
    const card = layout.cards.get(selected)
    if (!card) return
    const key = `${selected}:${card.stage}:${card.x}:${card.y}`
    if (key === revealed.current) return
    const opening = !revealed.current.startsWith(`${selected}:`)
    revealed.current = key
    clearTimeout(revealTimer.current)
    revealTimer.current = setTimeout(() => reveal(selected), opening ? 300 : 60)
  }, [selected, layout])
  useEffect(() => () => clearTimeout(revealTimer.current), [])

  // A trackpad scroll pans; a pinch (which arrives as ctrl + wheel) or
  // cmd + scroll zooms. React's wheel listener is passive, and a passive one
  // cannot stop the page from zooming along.
  useEffect(() => {
    const element = viewport.current
    if (!element) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      if (event.ctrlKey || event.metaKey) zoomAt(event.clientX, event.clientY, Math.exp(-event.deltaY * 0.01))
      else setView((was) => ({ ...was, x: was.x - event.deltaX, y: was.y - event.deltaY }))
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [])

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    const target = event.target as Element
    if (target.closest('button, a, input, textarea')) return
    const card = target.closest<HTMLElement>('[data-card]')
    if (card) {
      const id = Number(card.dataset.card)
      const placed = layout.cards.get(id)
      // Not on the server yet: there is nothing to open or move until it is.
      if (!placed || id < 0) return
      const at = absolute(placed)
      const point = toWorld(event.clientX, event.clientY)
      gesture.current = {
        kind: 'card',
        id,
        stage: placed.stage,
        x: event.clientX,
        y: event.clientY,
        grab: { x: point.x - at.x, y: point.y - at.y },
        moved: false,
      }
    } else {
      gesture.current = { kind: 'pan', x: event.clientX, y: event.clientY, view: current.current, moved: false }
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current
    if (!g) return
    if (!g.moved && Math.hypot(event.clientX - g.x, event.clientY - g.y) < 4) return
    g.moved = true
    if (g.kind === 'pan') {
      setPanning(true)
      setView({ ...g.view, x: g.view.x + event.clientX - g.x, y: g.view.y + event.clientY - g.y })
      return
    }
    const idea = ideas.find((item) => item.id === g.id)
    if (!idea) return
    const point = toWorld(event.clientX, event.clientY)
    const height = heightOf(idea, 'ideas')
    const target = stageAt(point, layout.heights)
    const refused = target !== null && target !== g.stage && (target === 'post' || g.stage === 'post')
    setDrag({
      id: g.id,
      from: g.stage,
      ghost: { x: point.x - Math.min(g.grab.x, CARD_WIDTH - 24), y: point.y - Math.min(g.grab.y, height - 12) },
      target,
      refused,
      index:
        target === 'filming' && !refused
          ? insertionIndex(layout, ids.filter((id) => id !== g.id), point.y)
          : undefined,
    })
  }

  const onPointerUp = () => {
    const g = gesture.current
    gesture.current = null
    setPanning(false)
    if (!g) return
    if (g.kind === 'pan') {
      if (!g.moved && selected !== null) navigate('/')
      return
    }
    if (!g.moved) {
      panelStore.set('open')
      navigate(`/idea/${g.id}`)
      return
    }
    if (dragRef.current) drop(dragRef.current)
    setDrag(null)
  }

  const drop = (d: Drag) => {
    const idea = ideas.find((item) => item.id === d.id)
    if (!idea) return
    letGo.current.set(d.id, d.ghost)
    if (!d.target) return
    if (d.refused) {
      toast(d.from === 'post' ? 'This one is filmed already' : 'Videos arrive in To post from the edit')
      return
    }
    if (isFree(d.target)) {
      const inside = { x: d.ghost.x - FRAMES[d.target].x, y: d.ghost.y }
      const point = freeSpot(layout, d.target, d.id, inside, heightOf(idea, d.target))
      // A frame still in its tidy grid is pinned where it is drawn, so the
      // rest stay put around the card that was placed by hand.
      const loose = unplaced(layout, d.target).filter((placement) => placement.id !== d.id)
      if (d.target === d.from) {
        void arrangeIdeas([...loose, { id: d.id, canvas: point }])
      } else {
        void arrangeIdeas(loose)
        moveToStage(idea, d.target, { at: point })
      }
      return
    }
    if (d.target === 'filming' && d.index !== undefined) {
      if (d.from !== 'filming') {
        moveToStage(idea, 'filming', { index: d.index })
        return
      }
      const reordered = ids.filter((id) => id !== d.id)
      reordered.splice(d.index, 0, d.id)
      if (reordered.join() !== ids.join()) void setQueue(reordered)
    }
  }

  const onDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    // Pointer capture sends the event to the viewport, so ask what is under it.
    if (document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-card], button, a')) return
    const point = toWorld(event.clientX, event.clientY)
    if (stageAt(point, layout.heights) !== 'ideas' || point.y < 0) return
    const inside = clampToFrame('ideas', { x: point.x - FRAMES.ideas.x - CARD_WIDTH / 2, y: point.y - 30 })
    void arrangeIdeas(unplaced(layout, 'ideas'))
    openQuickAdd('idea', undefined, freeSpot(layout, 'ideas', -1, inside, CHROME + LINE))
  }

  const tidy = (stage: Stage) => {
    const before: Placement[] = stages[stage]
      .filter((idea) => !isPending(idea))
      .map((idea) => ({ id: idea.id, canvas: idea.canvas }))
    void arrangeIdeas(before.map(({ id }) => ({ id, canvas: null })))
    toast(`${STAGE_LABEL[stage]} tidied up`, { action: { label: 'Undo', run: () => void arrangeIdeas(before) } })
  }

  const dragged = drag ? ideas.find((idea) => idea.id === drag.id) : undefined

  return (
    <div className="canvas" data-gliding={gliding || undefined}>
      <div
        ref={viewport}
        className="canvas-viewport"
        data-panning={panning || undefined}
        data-dragging={drag ? true : undefined}
        style={{
          backgroundSize: `${22 * view.zoom}px ${22 * view.zoom}px`,
          backgroundPosition: `${view.x}px ${view.y}px`,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        <div className="canvas-world" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}>
          <Arrows />
          {STAGES.map((stage) => (
            <Frame
              key={stage}
              stage={stage}
              ideas={stages[stage]}
              layout={layout}
              ready={phase === 'ready'}
              selected={selected}
              series={series}
              drag={drag}
              queue={ids}
              batchId={batch?.id ?? null}
              onTidy={() => tidy(stage)}
            />
          ))}
          {drag && dragged && (
            <div
              className="card"
              data-ghost
              style={{ left: drag.ghost.x, top: drag.ghost.y, width: CARD_WIDTH, height: heightOf(dragged, 'ideas') }}
            >
              <CardBody idea={dragged} stage="ideas" queue={ids} />
            </div>
          )}
        </div>
      </div>

      <div className="canvas-head">
        <PipelineHead ideas={inPipeline} />
      </div>

      <div className="canvas-zoom" role="group" aria-label="Zoom">
        <button type="button" aria-label="Zoom out" onClick={() => centreZoom(1 / 1.2)}>
          <MinusIcon size={16} />
        </button>
        <span className="canvas-zoom-level mono">{Math.round(view.zoom * 100)}%</span>
        <button type="button" aria-label="Zoom in" onClick={() => centreZoom(1.2)}>
          <PlusIcon size={16} />
        </button>
        <button type="button" aria-label="Show everything" onClick={fit}>
          <FitIcon size={16} />
        </button>
      </div>

      {phase === 'error' && (
        <div className="canvas-error">
          <p>{error ?? 'Could not load ideas.'}</p>
          <button type="button" className="button" onClick={() => void loadIdeas()}>
            Try again
          </button>
        </div>
      )}
    </div>
  )

  function centreZoom(factor: number) {
    const box = viewport.current?.getBoundingClientRect()
    if (!box) return
    glide()
    zoomAt(box.left + box.width / 2, box.top + box.height / 2, factor)
  }
}

/**
 * The cards of a frame that sit in its tidy grid, as placements where they
 * are drawn. An idea still on its way to the server is left out: the server
 * refuses a list with an id it does not know.
 */
function unplaced(layout: Layout, stage: Stage): Placement[] {
  const ideas = ideasStore.get().ideas
  return pinFrame(layout, stage).filter(
    (placement) => placement.id > 0 && !ideas.find((idea) => idea.id === placement.id)?.canvas,
  )
}

function Arrows() {
  const y = 60
  return (
    <svg className="canvas-arrows" width="1" height="1" aria-hidden="true">
      {STAGES.slice(1).map((stage, index) => {
        const from = FRAMES[STAGES[index]]
        const x1 = from.x + from.width + 18
        const x2 = FRAMES[stage].x - 18
        return (
          <g key={stage}>
            <path d={`M${x1} ${y} H${x2}`} />
            <path d={`M${x2 - 7} ${y - 6} L${x2} ${y} L${x2 - 7} ${y + 6}`} />
          </g>
        )
      })}
    </svg>
  )
}

const EMPTY: Record<Stage, string> = {
  ideas: 'No ideas here. Double-click to add one.',
  scripted: 'Drag an idea here once it has a script.',
  filming: 'Drag ideas here to build the next batch. Their order is the order you read them in.',
  post: 'Finished videos land here from the edit.',
}

function Frame({
  stage,
  ideas,
  layout,
  ready,
  selected,
  series,
  drag,
  queue,
  batchId,
  onTidy,
}: {
  stage: Stage
  ideas: Idea[]
  layout: Layout
  ready: boolean
  selected: number | null
  series: string
  drag: Drag | null
  queue: number[]
  batchId: number | null
  onTidy: () => void
}) {
  const frame = FRAMES[stage]
  const height = layout.heights[stage]
  const over = drag?.target === stage && drag.target !== drag.from
  const arranged = isFree(stage) && ideas.some((idea) => idea.canvas)
  const others = queue.filter((id) => id !== drag?.id)
  const insertAt =
    stage === 'filming' && drag?.target === 'filming' && drag.index !== undefined ? insertLine(layout, others, drag.index) : null

  return (
    <section
      className="frame"
      data-stage={stage}
      data-over={(over && !drag?.refused) || undefined}
      data-refuse={(over && drag?.refused) || undefined}
      style={{ left: frame.x, top: 0, width: frame.width, height }}
      aria-label={STAGE_LABEL[stage]}
    >
      <div className="frame-tab">
        <h2 className="frame-title">{STAGE_LABEL[stage]}</h2>
        <span className="frame-count mono">{ideas.length}</span>
        {arranged && (
          <button type="button" className="frame-action" onClick={onTidy}>
            Tidy up
          </button>
        )}
        {stage === 'filming' && ideas.length > 0 && (
          <button
            type="button"
            className="frame-start"
            disabled={batchId === null}
            onClick={() => batchId !== null && navigate(`/film/${batchId}`)}
          >
            <PlayIcon size={11} />
            Start filming
          </button>
        )}
      </div>
      {layout.labels
        .filter((label) => label.stage === stage)
        .map((label) => (
          <p key={label.text} className="frame-label" style={{ left: label.x, top: label.y + 4 }}>
            {label.text}
          </p>
        ))}
      {ready && !ideas.length && (
        <p className="frame-empty" style={{ height: Math.min(150, height - 2 * PAD) }}>
          {EMPTY[stage]}
        </p>
      )}
      {ideas.map((idea) => {
        const card = layout.cards.get(idea.id)
        if (!card) return null
        return (
          // A div rather than a <button>: a finished video's card holds the
          // posted rings, and a button may not hold buttons. It still takes
          // focus, and Enter opens it.
          <div
            key={idea.id}
            role="button"
            tabIndex={0}
            className="card"
            data-card={idea.id}
            data-stage={stage}
            data-selected={selected === idea.id || undefined}
            data-dim={(series !== 'all' && idea.series !== series) || undefined}
            data-lifted={drag?.id === idea.id || undefined}
            data-pending={isPending(idea) || undefined}
            aria-label={idea.title}
            style={{ left: card.x, top: card.y, width: card.width, height: card.height }}
            onKeyDown={(event) => {
              // Enter on a posted ring inside the card is the ring's.
              if (event.target !== event.currentTarget || isPending(idea)) return
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              panelStore.set('open')
              navigate(`/idea/${idea.id}`)
            }}
          >
            <CardBody idea={idea} stage={stage} queue={queue} />
          </div>
        )
      })}
      {insertAt !== null && <span className="frame-insert" style={{ top: insertAt }} />}
      {stage === 'ideas' && ready && ideas.length > 0 && <p className="frame-hint">Double-click an empty spot to add an idea here</p>}
    </section>
  )
}

function insertLine(layout: Layout, queue: number[], index: number): number {
  const at = layout.cards.get(queue[index])
  if (at) return at.y - GAP / 2
  const last = layout.cards.get(queue[queue.length - 1])
  return last ? last.y + last.height + GAP / 2 : PAD - 6
}

function CardBody({ idea, stage, queue }: { idea: Idea; stage: Stage; queue: number[] }) {
  if (stage === 'post') {
    return (
      <>
        <VideoThumb idea={idea} />
        <span className="card-text">
          <span className="card-title">{idea.title}</span>
          <IdeaMeta idea={idea} />
          {idea.status === 'edited' ? <VideoRings idea={idea} /> : <span className="card-note">In the edit</span>}
        </span>
      </>
    )
  }
  return (
    <>
      {stage === 'filming' && <span className="batch-number mono">{queue.indexOf(idea.id) + 1}</span>}
      <span className="card-text">
        <span className="card-title">{idea.title}</span>
        <IdeaMeta idea={idea} />
      </span>
    </>
  )
}
